/**
 * Iter 188 — Chartick Suite.
 *
 * Single React page with 6 tabs matching chartick.ai's product surface:
 *   1. Image Scanner   — drag-drop any chart image → vision LLM analysis
 *   2. Deep Dive       — 4-angle breakdown (trend/momentum/structure/context)
 *   3. Watchlist       — add/remove assets, see live quotes
 *   4. Smart Alerts    — price-level watchers (above/below/cross) + Telegram
 *   5. Daily Brief     — AI-generated morning digest
 *   6. Hot Markets     — top movers across cached OTC assets
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { analyseChartImage, hasPuter, PUTER_VISION_MODELS,
         generateDeepDiveAngles, generateBriefNarrative } from "../services/puterAI";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Upload, Microscope, Star, Bell, Newspaper, Flame,
  Trash2, Loader2, Plus, TrendingUp, TrendingDown, Minus, RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/chartick`;
const USER_ID = "default";

/* ─────────────────────── 1. Image Scanner ─────────────────────── */
function ImageOverlay({ src, levels, trendlines, tradeSetup }) {
  const containerRef = React.useRef(null);
  const [dims, setDims] = React.useState({ w: 0, h: 0 });
  // Hidden-items state: Set of overlay keys like "lv-3" or "tl-1" or "entry"
  // Reset whenever the source image changes.
  const [hidden, setHidden] = React.useState(() => new Set());
  React.useEffect(() => { setHidden(new Set()); }, [src]);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (!containerRef.current) return;
      const containerW = containerRef.current.clientWidth || img.width;
      const scale = containerW / img.width;
      setDims({ w: containerW, h: Math.round(img.height * scale) });
    };
    img.src = src;
  }, [src]);

  const toggle = React.useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  if (!src) return null;
  const safeLevels = Array.isArray(levels) ? levels : [];
  const safeTrendlines = Array.isArray(trendlines) ? trendlines : [];
  const ts = tradeSetup && tradeSetup.entry ? tradeSetup : null;
  const tpCount = ts ? (ts.take_profits || []).length : 0;
  const tradeKeys = ts ? ["entry", "sl", ...Array.from({length: tpCount}, (_, i) => `tp-${i}`)] : [];
  const totalItems = safeLevels.length + safeTrendlines.length + tradeKeys.length;
  const hiddenCount = hidden.size;

  const kindColor = (kind) =>
      kind === "resistance" ? "#f87171"
    : kind === "support"    ? "#4ade80"
    : "#facc15";
  const dashFor = (strength) =>
      strength >= 3 ? "0"
    : strength === 2 ? "6,3"
    : "3,3";

  return (
    <div>
      <div ref={containerRef} className="relative rounded overflow-hidden mx-auto"
           style={{ maxWidth: 720 }} data-testid="chartick-overlay">
        <img src={src} alt="chart" className="block w-full h-auto"
             data-testid="chartick-preview" />
        <svg className="absolute inset-0 w-full h-full"
             viewBox={`0 0 ${dims.w || 100} ${dims.h || 100}`}
             preserveAspectRatio="none"
             data-testid="chartick-overlay-svg"
             style={{ pointerEvents: "none" }}>
          {/* Trade setup zones — Entry band + Stop-Loss + Take-Profits */}
          {ts && !hidden.has("entry") && (() => {
            const yMin = (ts.entry.y_pct_min || 0) * (dims.h || 0);
            const yMax = (ts.entry.y_pct_max || 0) * (dims.h || 0);
            const h = Math.max(2, yMax - yMin);
            return (
              <g key="entry-zone" data-testid="chartick-entry-zone"
                 onClick={() => toggle("entry")}
                 style={{ cursor: "pointer", pointerEvents: "auto" }}>
                <rect x={0} y={yMin} width={dims.w} height={h}
                      fill="rgba(251, 191, 36, 0.18)"
                      stroke="rgba(251, 191, 36, 0.8)" strokeWidth={1}
                      strokeDasharray="4,2" />
                <g transform={`translate(8, ${yMin + h / 2})`}>
                  <rect x={0} y={-9} rx={3} height={16}
                        width={ts.entry.price != null ? 100 : 60}
                        fill="rgba(146, 64, 14, 0.95)" />
                  <text x={5} y={2} fontSize={10}
                        fontFamily="ui-monospace, monospace"
                        fill="#fef3c7" dominantBaseline="middle"
                        fontWeight="700">
                    ENTRY{ts.entry.price != null ? ` · ${ts.entry.price}` : ""}
                  </text>
                </g>
              </g>
            );
          })()}
          {ts && !hidden.has("sl") && (() => {
            const y = (ts.stop_loss.y_pct || 0) * (dims.h || 0);
            // Shaded "danger" band from entry to SL
            const entryEdge = ts.direction === "BUY"
              ? (ts.entry.y_pct_max || 0) * (dims.h || 0)   // SL below (y greater)
              : (ts.entry.y_pct_min || 0) * (dims.h || 0);  // SL above (y smaller)
            const yTop = Math.min(entryEdge, y);
            const yBot = Math.max(entryEdge, y);
            return (
              <g key="sl-zone" data-testid="chartick-sl-zone"
                 onClick={() => toggle("sl")}
                 style={{ cursor: "pointer", pointerEvents: "auto" }}>
                <rect x={0} y={yTop} width={dims.w} height={Math.max(1, yBot - yTop)}
                      fill="rgba(239, 68, 68, 0.12)" />
                <line x1={0} y1={y} x2={dims.w} y2={y}
                      stroke="#ef4444" strokeWidth={1.6}
                      strokeDasharray="2,2" />
                <g transform={`translate(8, ${y})`}>
                  <rect x={0} y={-9} rx={3} height={16}
                        width={ts.stop_loss.price != null ? 100 : 44}
                        fill="rgba(136, 19, 55, 0.95)" />
                  <text x={5} y={2} fontSize={10}
                        fontFamily="ui-monospace, monospace"
                        fill="#fef3c7" dominantBaseline="middle"
                        fontWeight="700">
                    SL{ts.stop_loss.price != null ? ` · ${ts.stop_loss.price}` : ""}
                  </text>
                </g>
              </g>
            );
          })()}
          {ts && (ts.take_profits || []).map((tp, i) => {
            const key = `tp-${i}`;
            if (hidden.has(key)) return null;
            const y = (tp.y_pct || 0) * (dims.h || 0);
            // Shaded "reward" band between entry and this TP
            const entryEdge = ts.direction === "BUY"
              ? (ts.entry.y_pct_min || 0) * (dims.h || 0)
              : (ts.entry.y_pct_max || 0) * (dims.h || 0);
            const yTop = Math.min(entryEdge, y);
            const yBot = Math.max(entryEdge, y);
            const fade = 0.08 + (0.04 * i);     // slightly fade further TPs
            return (
              <g key={key} data-testid={`chartick-tp-zone-${i}`}
                 onClick={() => toggle(key)}
                 style={{ cursor: "pointer", pointerEvents: "auto" }}>
                <rect x={0} y={yTop} width={dims.w}
                      height={Math.max(1, yBot - yTop)}
                      fill={`rgba(16, 185, 129, ${fade})`} />
                <line x1={0} y1={y} x2={dims.w} y2={y}
                      stroke="#10b981" strokeWidth={1.6}
                      strokeDasharray="2,2" />
                <g transform={`translate(${(dims.w || 0) - 110}, ${y})`}>
                  <rect x={0} y={-9} rx={3} height={16}
                        width={tp.price != null ? 100 : 44}
                        fill="rgba(6, 78, 59, 0.95)" />
                  <text x={5} y={2} fontSize={10}
                        fontFamily="ui-monospace, monospace"
                        fill="#d1fae5" dominantBaseline="middle"
                        fontWeight="700">
                    {tp.label}{tp.price != null ? ` · ${tp.price}` : ""}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Horizontal levels */}
          {safeLevels.map((lv, i) => {
            const key = `lv-${i}`;
            if (hidden.has(key)) return null;
            const yPx = (lv.y_pct || 0) * (dims.h || 0);
            const stroke = kindColor(lv.kind);
            const w = Math.max(1.2, (lv.strength || 1) * 0.9);
            const labelBg = lv.kind === "resistance"
              ? "rgba(136,19,55,0.9)" : "rgba(6,78,59,0.9)";
            const labelW = 56 + (lv.price != null ? 50 : 0);
            return (
              <g key={key} data-testid={`chartick-level-${i}`}
                 onClick={() => toggle(key)}
                 style={{ cursor: "pointer", pointerEvents: "auto" }}>
                {/* Thick invisible hit-target behind the line so clicks are easy */}
                <line x1={0} y1={yPx} x2={dims.w} y2={yPx}
                      stroke="transparent" strokeWidth={14} />
                <line x1={0} y1={yPx} x2={dims.w} y2={yPx}
                      stroke={stroke} strokeWidth={w}
                      strokeDasharray={dashFor(lv.strength)} opacity={0.9} />
                <g transform={`translate(8, ${yPx})`}>
                  <rect x={0} y={-9} rx={3} height={16}
                        width={labelW} fill={labelBg} />
                  <text x={5} y={2} fontSize={10}
                        fontFamily="ui-monospace, monospace"
                        fill="#fef3c7" dominantBaseline="middle">
                    {lv.label}{lv.price != null ? ` · ${lv.price}` : ""}
                  </text>
                </g>
              </g>
            );
          })}
          {/* Trendlines */}
          {safeTrendlines.map((tl, i) => {
            const key = `tl-${i}`;
            if (hidden.has(key)) return null;
            const x1 = (tl.x1 || 0) * (dims.w || 0);
            const y1 = (tl.y1 || 0) * (dims.h || 0);
            const x2 = (tl.x2 || 0) * (dims.w || 0);
            const y2 = (tl.y2 || 0) * (dims.h || 0);
            const stroke = kindColor(tl.kind);
            const w = Math.max(1.4, (tl.strength || 1) * 1.1);
            // Label anchor at the right endpoint, nudged inward
            const anchorX = x2 >= x1 ? x2 : x1;
            const anchorY = x2 >= x1 ? y2 : y1;
            const bgColor = tl.kind === "resistance" ? "rgba(136,19,55,0.9)"
                            : tl.kind === "support"  ? "rgba(6,78,59,0.9)"
                            : "rgba(120,53,15,0.9)";
            return (
              <g key={key} data-testid={`chartick-trendline-${i}`}
                 onClick={() => toggle(key)}
                 style={{ cursor: "pointer", pointerEvents: "auto" }}>
                <line x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke="transparent" strokeWidth={14} />
                <line x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke={stroke} strokeWidth={w} opacity={0.9}
                      strokeDasharray={dashFor(tl.strength)} />
                <circle cx={x1} cy={y1} r={3} fill={stroke} />
                <circle cx={x2} cy={y2} r={3} fill={stroke} />
                <g transform={`translate(${Math.max(10, anchorX - 56)}, ${anchorY - 10})`}>
                  <rect x={0} y={-9} rx={3} height={16} width={54} fill={bgColor} />
                  <text x={5} y={2} fontSize={10}
                        fontFamily="ui-monospace, monospace"
                        fill="#fef3c7" dominantBaseline="middle">
                    {tl.label}
                  </text>
                </g>
              </g>
            );
          })}
        </svg>
      </div>
      {totalItems > 0 && (
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400"
             data-testid="chartick-overlay-controls">
          <span>
            {totalItems - hiddenCount}/{totalItems} visible
            {hiddenCount > 0 && (
              <>
                {" · "}
                <button className="text-amber-400 hover:underline"
                        data-testid="chartick-overlay-reset"
                        onClick={() => setHidden(new Set())}>show all</button>
              </>
            )}
          </span>
          {ts?.risk_reward != null && (
            <span className="text-amber-400 font-mono"
                  data-testid="chartick-rr-badge">
              R:R {ts.risk_reward}
            </span>
          )}
          <span className="text-slate-600">
            tip: click any line or zone to toggle it off
          </span>
        </div>
      )}
    </div>
  );
}

function ImageScannerTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assetHint, setAssetHint] = useState("");
  const [tfHint, setTfHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [visionModel, setVisionModel] = useState(() => {
    try { return localStorage.getItem("puter:visionModel") || "gpt-5.6-luna"; }
    catch (_e) { return "gpt-5.6-luna"; }
  });
  useEffect(() => {
    try { localStorage.setItem("puter:visionModel", visionModel); } catch (_e) {}
  }, [visionModel]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File > 10MB"); return; }
    setFile(f); setResult(null); setError(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  }, []);

  async function analyze() {
    if (!preview) { toast.error("Upload a chart image first"); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      if (!hasPuter()) {
        throw new Error("puter.js not loaded — reload the page or check your network.");
      }
      // Iter 193 — AI runs entirely client-side via Puter. The user's Puter
      // wallet is billed; no server-side key, no backend proxy.
      const r = await analyseChartImage(preview, {
        assetHint: assetHint || "",
        tfHint: tfHint || "",
        model: visionModel,
      });
      setResult(r);
    } catch (e) {
      const raw = e?.message || "scan failed";
      // Puter sign-in prompt → let it surface verbatim; wallet-empty → friendly.
      const isBalance = /insufficient|out of credit|balance|wallet/i.test(raw);
      setError(isBalance
        ? "🪫 Your Puter wallet is out of credits. Top up at https://puter.com/pay then retry."
        : raw);
    } finally { setLoading(false); }
  }

  const dirColor = result?.direction === "BUY"
    ? "bg-emerald-900/60 text-emerald-200 border-emerald-500/60"
    : result?.direction === "SELL"
      ? "bg-rose-900/60 text-rose-200 border-rose-500/60"
      : "bg-slate-800 text-slate-300 border-slate-600";
  const levelCount = (result?.levels || []).length;
  const trendlineCount = (result?.trendlines || []).length;
  const tradeSetup = result?.trade_setup || null;
  const tradeItemCount = tradeSetup
    ? 1 /* entry */ + 1 /* sl */ + (tradeSetup.take_profits || []).length
    : 0;
  const overlayCount = levelCount + trendlineCount + tradeItemCount;

  return (
    <div className="space-y-4" data-testid="chartick-image-scanner">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => document.getElementById("chartick-file-input")?.click()}
            className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500 transition-colors"
            data-testid="chartick-dropzone"
          >
            {preview ? (
              (result && showOverlay && overlayCount > 0)
                ? <ImageOverlay src={preview} levels={result.levels}
                                 trendlines={result.trendlines}
                                 tradeSetup={tradeSetup} />
                : <img src={preview} alt="chart"
                       className="max-h-80 mx-auto rounded"
                       data-testid="chartick-preview-plain" />
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-slate-500 mb-2" />
                <p className="text-slate-300 text-sm">Drag &amp; drop any chart image</p>
                <p className="text-slate-500 text-xs mt-1">
                  PNG · JPG · WEBP · HEIC (max 10MB) · works with PO / TradingView / MT4-5 / brokerage
                </p>
              </>
            )}
            <input id="chartick-file-input" type="file" accept="image/*"
                   className="hidden" onChange={onDrop}
                   data-testid="chartick-file-input" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Input placeholder="Asset hint (optional) — e.g. EURUSD"
                   value={assetHint} onChange={(e) => setAssetHint(e.target.value)}
                   className="bg-white border-slate-700 text-black placeholder:text-slate-500"
                   data-testid="chartick-asset-hint" />
            <Input placeholder="Timeframe hint (optional) — e.g. 1m, 5s, 1h"
                   value={tfHint} onChange={(e) => setTfHint(e.target.value)}
                   className="bg-white border-slate-700 text-black placeholder:text-slate-500"
                   data-testid="chartick-tf-hint" />
          </div>
          <div className="flex items-center gap-3 mt-4">
            <select value={visionModel}
                    onChange={(e) => setVisionModel(e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100"
                    data-testid="chartick-vision-model">
              {PUTER_VISION_MODELS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <Button onClick={analyze} disabled={!preview || loading}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                    data-testid="chartick-analyze-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Microscope className="h-4 w-4 mr-2" />}
              {loading ? "Analyzing via Puter…" : "Analyze Chart (Puter AI)"}
            </Button>
            {result && overlayCount > 0 && (
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer"
                     data-testid="chartick-overlay-toggle">
                <input type="checkbox" checked={showOverlay}
                       onChange={(e) => setShowOverlay(e.target.checked)} />
                show overlay ({levelCount} level{levelCount === 1 ? "" : "s"}
                {trendlineCount > 0 && <> · {trendlineCount} trendline{trendlineCount === 1 ? "" : "s"}</>}
                {tradeSetup && <> · Entry/SL/{(tradeSetup.take_profits || []).length} TP</>})
              </label>
            )}
          </div>
          {error && <div className="mt-3 p-2 bg-rose-900/40 text-rose-200 text-sm rounded"
                         data-testid="chartick-error">{error}</div>}
        </CardContent>
      </Card>

      {result && (
        <Card className="bg-slate-900/60 border-slate-800" data-testid="chartick-image-result">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 rounded-full border font-black tracking-wider ${dirColor}`}>
                {result.direction}
              </div>
              <span className="text-slate-400 text-sm">
                · {result.quality} · {(result.confidence * 100).toFixed(1)}%
              </span>
              <span className="flex-1" />
              <span className="text-xs text-slate-500">
                👁 vision · {result.latency_ms}ms
                {overlayCount > 0 && <> · {overlayCount} overlay items</>}
              </span>
            </div>
            {result.narrative && (
              <div className="p-3 bg-slate-800/60 border-l-2 border-amber-500 text-sm text-slate-200">
                {result.narrative}
              </div>
            )}
            {result.bullets?.length > 0 && (
              <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">
                {result.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <div className="p-2 bg-slate-800/40 rounded"><span className="text-slate-500">Resistance:</span><br/><span className="font-mono text-rose-300">{(result.resistance || []).join(" · ") || "—"}</span></div>
              <div className="p-2 bg-slate-800/40 rounded"><span className="text-slate-500">Support:</span><br/><span className="font-mono text-emerald-300">{(result.support || []).join(" · ") || "—"}</span></div>
              <div className="p-2 bg-slate-800/40 rounded"><span className="text-slate-500">Patterns:</span><br/><span className="font-mono text-slate-300">{(result.patterns || []).join(", ") || "—"}</span></div>
            </div>
            {tradeSetup && (
              <div className="p-3 bg-gradient-to-r from-amber-900/40 to-slate-800/40 border border-amber-500/40 rounded"
                   data-testid="chartick-trade-setup-card">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-black text-amber-400 text-sm tracking-wider">
                    TRADE SETUP · {tradeSetup.direction}
                  </span>
                  {tradeSetup.risk_reward != null && (
                    <span className="text-xs font-mono text-amber-300">
                      R:R {tradeSetup.risk_reward}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="p-2 bg-slate-900/60 rounded">
                    <div className="text-slate-500">Entry</div>
                    <div className="font-mono text-amber-200">
                      {tradeSetup.entry.price ?? "—"}
                    </div>
                  </div>
                  <div className="p-2 bg-slate-900/60 rounded">
                    <div className="text-slate-500">Stop Loss</div>
                    <div className="font-mono text-rose-300">
                      {tradeSetup.stop_loss.price ?? "—"}
                    </div>
                  </div>
                  {(tradeSetup.take_profits || []).map((tp, i) => (
                    <div key={i} className="p-2 bg-slate-900/60 rounded"
                         data-testid={`chartick-tp-${i}`}>
                      <div className="text-slate-500">{tp.label}</div>
                      <div className="font-mono text-emerald-300">
                        {tp.price ?? "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────── 2. Deep Dive ─────────────────────── */
function DeepDiveTab() {
  const [asset, setAsset] = useState("EURUSD_OTC");
  const [tf, setTf] = useState("5s");
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function enrichWithPuter(base) {
    if (!hasPuter()) return;
    setEnriching(true);
    try {
      const puterOut = await generateDeepDiveAngles({
        tech_summary: base.tech_summary,
        ensemble_summary: base.ensemble_summary,
        asset: base.asset, timeframe: base.timeframe,
      });
      if (!puterOut) {
        toast.info("Puter returned no parseable JSON — showing deterministic angles.");
        return;
      }
      // Merge angles by name, prefer Puter summary/bullets but keep deterministic score when Puter is 0
      const byName = new Map((base.angles || []).map((a) => [a.name, a]));
      const mergedAngles = [];
      ["trend", "momentum", "structure", "context"].forEach((name) => {
        const det = byName.get(name) || {};
        const llm = puterOut.angles.find((a) => a.name === name) || {};
        mergedAngles.push({
          name,
          summary: llm.summary || det.summary || "",
          bullets: (llm.bullets && llm.bullets.length) ? llm.bullets : (det.bullets || []),
          score: Number.isFinite(llm.score) ? llm.score : (det.score || 0),
          llm: Boolean(llm.summary),
        });
      });
      const composite = Number.isFinite(puterOut.composite_score)
        ? puterOut.composite_score
        : mergedAngles.reduce((s, a) => s + (a.score || 0), 0);
      setResult((prev) => ({
        ...(prev || base),
        angles: mergedAngles,
        composite_score: composite,
        direction: puterOut.composite_direction || prev?.direction,
        confidence: Number.isFinite(puterOut.composite_confidence) && puterOut.composite_confidence > 0
          ? puterOut.composite_confidence : prev?.confidence,
        llm_used: true,
        llm_source: "puter",
        llm_model: puterOut.model,
      }));
    } catch (e) {
      toast.error(`Puter deep-dive failed: ${e.message || e}`);
    } finally {
      setEnriching(false);
    }
  }

  async function run() {
    setLoading(true); setError(null); setResult(null);
    try {
      const barsRes = await axios.get(
        `${BACKEND_URL}/api/historical/candles/${asset}/${tf}`,
        { params: { limit: 100 } },
      );
      const bars = (barsRes.data?.candles || [])
        .map((b) => ({ open: +b.open, high: +b.high, low: +b.low, close: +b.close, volume: +b.volume || 0 }))
        .filter((b) => b.open > 0 && b.close > 0);
      if (bars.length < 10) throw new Error(`Only ${bars.length} bars found`);
      const r = await axios.post(`${API}/deep-dive`,
        { asset, timeframe: tf, bars, user_id: USER_ID },
        { timeout: 60000 });
      setResult(r.data);
      // Fire-and-forget Puter enrichment
      enrichWithPuter(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="space-y-4" data-testid="chartick-deep-dive">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-slate-500 block mb-1">Asset</label>
            <Input value={asset} onChange={(e) => setAsset(e.target.value.toUpperCase())}
                   className="bg-white border-slate-700 text-black font-mono placeholder:text-slate-500"
                   data-testid="deep-dive-asset" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Timeframe</label>
            <select value={tf} onChange={(e) => setTf(e.target.value)}
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                    data-testid="deep-dive-tf">
              {["5s", "15s", "30s", "1m"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <Button onClick={run} disabled={loading}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                  data-testid="deep-dive-run-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Microscope className="h-4 w-4 mr-2" />}
            {loading ? "Deep Diving…" : "Run Deep Dive"}
          </Button>
        </CardContent>
      </Card>

      {error && <div className="p-3 bg-rose-900/40 text-rose-200 text-sm rounded">{error}</div>}

      {result && (
        <>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-5 flex items-center gap-3">
              <div className={`px-4 py-2 rounded-full border font-black tracking-wider ${
                result.direction === "BUY" ? "bg-emerald-900/60 text-emerald-200 border-emerald-500/60" :
                result.direction === "SELL" ? "bg-rose-900/60 text-rose-200 border-rose-500/60" :
                "bg-slate-800 text-slate-300 border-slate-600"
              }`}>{result.direction}</div>
              <span className="text-slate-400 text-sm">·{result.confidence * 100 | 0}% · composite {result.composite_score}</span>
              <span className="flex-1" />
              <span className="text-xs text-slate-500">{result.llm_used ? `✓ Puter (${result.llm_model || "AI"})` : (enriching ? "enriching…" : "deterministic")} · {result.latency_ms}ms</span>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(result.angles || []).map((a) => (
              <Card key={a.name} className="bg-slate-900/60 border-slate-800" data-testid={`deep-dive-angle-${a.name}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-200 flex items-center justify-between">
                    <span className="uppercase tracking-wider text-amber-500">{a.name}</span>
                    <Badge className={a.score > 0 ? "bg-emerald-900/60 text-emerald-300" : a.score < 0 ? "bg-rose-900/60 text-rose-300" : "bg-slate-800 text-slate-300"}>
                      {a.score > 0 ? `+${a.score}` : a.score}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  <p className="text-sm text-slate-300 leading-relaxed">{a.summary}</p>
                  {a.bullets?.length > 0 && (
                    <ul className="list-disc pl-5 text-xs text-slate-400 space-y-1">
                      {a.bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────── 3. Watchlist ─────────────────────── */
function WatchlistTab() {
  const [assets, setAssets] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [newAsset, setNewAsset] = useState("");

  async function reload() {
    try {
      const r = await axios.get(`${API}/watchlist/quotes`, { params: { user_id: USER_ID } });
      setQuotes(r.data?.quotes || []);
      setAssets((r.data?.quotes || []).map((q) => q.asset));
    } catch (_e) { /* ignore */ }
  }

  useEffect(() => { reload(); const h = setInterval(reload, 15000); return () => clearInterval(h); }, []);

  async function add() {
    if (!newAsset.trim()) return;
    await axios.post(`${API}/watchlist/add`, { user_id: USER_ID, asset: newAsset });
    setNewAsset("");
    toast.success(`Added ${newAsset.toUpperCase()}`);
    reload();
  }

  async function remove(a) {
    await axios.post(`${API}/watchlist/remove`, { user_id: USER_ID, asset: a });
    toast.success(`Removed ${a}`);
    reload();
  }

  return (
    <div className="space-y-4" data-testid="chartick-watchlist">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 flex gap-2">
          <Input value={newAsset} onChange={(e) => setNewAsset(e.target.value.toUpperCase())}
                 placeholder="EURUSD_OTC"
                 className="bg-white border-slate-700 text-black font-mono placeholder:text-slate-500"
                 data-testid="watchlist-input" />
          <Button onClick={add} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                  data-testid="watchlist-add-btn">
            <Plus className="h-4 w-4 mr-1"/> Add
          </Button>
          <Button onClick={reload} variant="outline" className="border-slate-700" data-testid="watchlist-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {quotes.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-8">
            No assets yet. Add one above.
          </div>
        )}
        {quotes.map((q) => {
          const up = (q.change_pct ?? 0) > 0;
          const flat = (q.change_pct ?? 0) === 0 || q.change_pct == null;
          const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
          const color = flat ? "text-slate-400" : up ? "text-emerald-400" : "text-rose-400";
          return (
            <Card key={q.asset} className="bg-slate-900/60 border-slate-800" data-testid={`wl-${q.asset}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-mono text-slate-100 font-bold">{q.asset}</div>
                  <div className={`text-sm flex items-center gap-1 ${color}`}>
                    <Icon className="h-3 w-3" /> {q.price ?? "—"}
                    {q.change_pct != null &&
                      <span className="text-xs ml-1">({q.change_pct > 0 ? "+" : ""}{q.change_pct}%)</span>}
                  </div>
                </div>
                <Button onClick={() => remove(q.asset)} size="sm" variant="ghost"
                        className="text-slate-500 hover:text-rose-400"
                        data-testid={`wl-remove-${q.asset}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── 4. Smart Alerts ─────────────────────── */
function AlertsTab() {
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ asset: "EURUSD_OTC", condition: "above", price: 1.1, note: "", telegram: false });

  async function reload() {
    try {
      const [a, e] = await Promise.all([
        axios.get(`${API}/alerts`, { params: { user_id: USER_ID } }),
        axios.get(`${API}/alerts/events`, { params: { user_id: USER_ID, limit: 20 } }),
      ]);
      setAlerts(a.data?.alerts || []);
      setEvents(e.data?.events || []);
    } catch (_e) { /* ignore */ }
  }

  useEffect(() => { reload(); const h = setInterval(reload, 15000); return () => clearInterval(h); }, []);

  async function createAlert() {
    try {
      await axios.post(`${API}/alerts`, { ...form, user_id: USER_ID });
      toast.success("Alert created");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }

  async function del(id) {
    await axios.delete(`${API}/alerts/${id}`, { params: { user_id: USER_ID } });
    reload();
  }

  async function scanNow() {
    const r = await axios.post(`${API}/alerts/scan-now`);
    toast.info(`Scanned ${r.data?.checked} alerts, ${r.data?.fired} fired`);
    reload();
  }

  return (
    <div className="space-y-4" data-testid="chartick-alerts">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-5 gap-2">
          <Input placeholder="asset" value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value.toUpperCase() })} className="bg-white border-slate-700 text-black font-mono placeholder:text-slate-500" data-testid="alert-asset" />
          <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100" data-testid="alert-cond">
            {["above", "below", "cross_up", "cross_down"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input type="number" step="0.0001" placeholder="price" value={form.price}
                 onChange={(e) => setForm({ ...form, price: +e.target.value })}
                 className="bg-white border-slate-700 text-black placeholder:text-slate-500" data-testid="alert-price" />
          <Input placeholder="note (optional)" value={form.note}
                 onChange={(e) => setForm({ ...form, note: e.target.value })}
                 className="bg-white border-slate-700 text-black placeholder:text-slate-500" data-testid="alert-note" />
          <div className="flex gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-400">
              <input type="checkbox" checked={form.telegram}
                     onChange={(e) => setForm({ ...form, telegram: e.target.checked })}
                     data-testid="alert-tg" /> TG
            </label>
            <Button onClick={createAlert} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold flex-1"
                    data-testid="alert-create-btn">
              <Bell className="h-4 w-4 mr-1" /> Create
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-300">Active &amp; Triggered ({alerts.length})</h3>
        <Button onClick={scanNow} size="sm" variant="outline" className="border-slate-700" data-testid="alerts-scan-now">
          <RefreshCw className="h-3 w-3 mr-1" /> Scan now
        </Button>
      </div>
      <div className="space-y-1">
        {alerts.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 bg-slate-900/40 border border-slate-800 rounded"
               data-testid={`alert-row-${a.id}`}>
            <Badge className={a.triggered ? "bg-rose-900/60 text-rose-300" : "bg-emerald-900/60 text-emerald-300"}>
              {a.triggered ? "fired" : "watching"}
            </Badge>
            <span className="font-mono text-slate-100">{a.asset}</span>
            <span className="text-sm text-slate-400">{a.condition} {a.price}</span>
            {a.telegram && <Badge variant="outline" className="border-sky-700 text-sky-400 text-[10px]">TG</Badge>}
            {a.note && <span className="text-xs text-slate-500 italic">· {a.note}</span>}
            <span className="flex-1" />
            <span className="text-xs text-slate-600">last: {a.last_price_seen ?? "—"}</span>
            <Button onClick={() => del(a.id)} size="sm" variant="ghost"
                    className="text-slate-500 hover:text-rose-400" data-testid={`alert-del-${a.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {events.length > 0 && (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Recent Fires</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-1">
            {events.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-800/50 last:border-0">
                <Badge className="bg-rose-900/60 text-rose-300">🚨</Badge>
                <span className="font-mono text-slate-100">{e.asset}</span>
                <span className="text-slate-400">{e.condition} {e.target}</span>
                <span className="text-slate-500">· fired @ {e.price_at_fire}</span>
                <span className="flex-1" />
                <span className="text-slate-600">{new Date(e.fired_at).toLocaleString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────── 5. Daily Brief ─────────────────────── */
function BriefTab() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [writing, setWriting] = useState(false);
  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const r = await axios.get(`${API}/daily-brief/latest`, { params: { user_id: USER_ID } });
      if (r.data?.ok) setBrief(r.data);
    } catch (_e) { /* ignore */ }
  }

  async function writeNarrative(briefData) {
    if (!hasPuter()) {
      toast.info("Puter not loaded — brief saved without narrative. Sign in to Puter to generate one.");
      return;
    }
    setWriting(true);
    try {
      const narrative = await generateBriefNarrative({
        watchlist_quotes: briefData.watchlist_quotes || [],
        hot_movers: briefData.hot_movers || [],
        recent_alerts: briefData.recent_alerts || [],
      });
      if (!narrative) {
        toast.info("Puter returned empty — brief saved without narrative.");
        return;
      }
      // Persist back to backend
      try {
        await axios.patch(`${API}/daily-brief/narrative`, {
          user_id: USER_ID, date: briefData.date, narrative,
        });
      } catch (e) {
        // Narrative still shown locally even if persistence fails
        console.warn("brief narrative persist failed", e);
      }
      setBrief((prev) => ({ ...(prev || briefData), narrative }));
      toast.success("Narrative written (Puter)");
    } catch (e) {
      toast.error(`Puter brief failed: ${e.message || e}`);
    } finally {
      setWriting(false);
    }
  }

  async function generate() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/daily-brief/generate`, null, { params: { user_id: USER_ID }, timeout: 45000 });
      setBrief(r.data);
      toast.success("Brief data generated — writing narrative…");
      writeNarrative(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-4" data-testid="chartick-brief">
      <div className="flex items-center justify-between">
        <h3 className="text-sm text-slate-300">
          {brief?.date ? `Brief · ${brief.date}` : "No brief yet"}
          {writing && <span className="ml-2 text-amber-400">· Puter writing…</span>}
        </h3>
        <div className="flex items-center gap-2">
          {brief && !brief.narrative && hasPuter() && (
            <Button onClick={() => writeNarrative(brief)} disabled={writing} size="sm" variant="outline"
                    className="border-slate-700" data-testid="brief-rewrite-btn">
              {writing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Write Narrative (Puter)
            </Button>
          )}
          <Button onClick={generate} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                  data-testid="brief-generate-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Newspaper className="h-4 w-4 mr-1" />}
            {loading ? "Generating…" : "Generate Today's Brief"}
          </Button>
        </div>
      </div>
      {brief && (
        <>
          {brief.narrative && (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-5 text-sm text-slate-200 leading-relaxed whitespace-pre-line"
                            data-testid="brief-narrative">
                {brief.narrative}
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Watchlist</CardTitle></CardHeader>
              <CardContent className="pt-0 space-y-1 text-xs">
                {(brief.watchlist_quotes || []).map((q) => (
                  <div key={q.asset} className="flex justify-between py-1">
                    <span className="font-mono text-slate-200">{q.asset}</span>
                    <span className={q.change_pct > 0 ? "text-emerald-400" : q.change_pct < 0 ? "text-rose-400" : "text-slate-400"}>
                      {q.price ?? "—"} ({q.change_pct ?? 0}%)
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Top Movers</CardTitle></CardHeader>
              <CardContent className="pt-0 space-y-1 text-xs">
                {(brief.hot_movers || []).map((m) => (
                  <div key={m.asset} className="flex justify-between py-1">
                    <span className="font-mono text-slate-200">{m.asset}</span>
                    <span className={m.change_pct > 0 ? "text-emerald-400" : "text-rose-400"}>
                      {m.change_pct > 0 ? "+" : ""}{m.change_pct}%
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────── 6. Hot Markets ─────────────────────── */
function HotMarketsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cached, setCached] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hot-markets`, { params: { limit: 20 } });
      setItems(r.data?.items || []);
      setCached(!!r.data?.cached);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }

  useEffect(() => { reload(); const h = setInterval(reload, 60000); return () => clearInterval(h); }, []);

  return (
    <div className="space-y-4" data-testid="chartick-hot">
      <div className="flex items-center justify-between">
        <h3 className="text-sm text-slate-300">
          Top {items.length} movers · {cached ? "cached" : "fresh"}
        </h3>
        <Button onClick={reload} disabled={loading} variant="outline" className="border-slate-700"
                data-testid="hot-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0 overflow-hidden rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60">
              <tr className="text-slate-400">
                <th className="text-left px-4 py-2">Asset</th>
                <th className="text-right px-4 py-2">Last</th>
                <th className="text-right px-4 py-2">Change %</th>
                <th className="text-right px-4 py-2">Volatility</th>
                <th className="text-right px-4 py-2">Bars</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const up = m.change_pct > 0;
                return (
                  <tr key={m.asset} className="border-t border-slate-800/50"
                      data-testid={`hot-row-${m.asset}`}>
                    <td className="px-4 py-2 font-mono text-slate-200">{m.asset}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-300">{m.last_close}</td>
                    <td className={`px-4 py-2 text-right font-mono ${up ? "text-emerald-400" : "text-rose-400"}`}>
                      {up ? "+" : ""}{m.change_pct}%
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-slate-400">{m.volatility}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{m.bars}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────────── Shell ─────────────────────── */
export default function ChartickSuite() {
  return (
    <div className="p-6 space-y-6" data-testid="chartick-suite">
      <div>
        <h1 className="text-3xl font-black text-slate-100 flex items-center gap-3">
          <Flame className="h-7 w-7 text-amber-500" />
          Chartick Suite
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          AI trading intelligence companion — chart analysis, deep dives, watchlist, alerts, daily brief, hot markets.
        </p>
      </div>

      <Tabs defaultValue="image" className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 inline-flex">
          <TabsTrigger value="image" data-testid="tab-image"><Upload className="h-4 w-4 mr-1"/>Image Scanner</TabsTrigger>
          <TabsTrigger value="deep" data-testid="tab-deep"><Microscope className="h-4 w-4 mr-1"/>Deep Dive</TabsTrigger>
          <TabsTrigger value="watch" data-testid="tab-watch"><Star className="h-4 w-4 mr-1"/>Watchlist</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="h-4 w-4 mr-1"/>Alerts</TabsTrigger>
          <TabsTrigger value="brief" data-testid="tab-brief"><Newspaper className="h-4 w-4 mr-1"/>Daily Brief</TabsTrigger>
          <TabsTrigger value="hot" data-testid="tab-hot"><Flame className="h-4 w-4 mr-1"/>Hot Markets</TabsTrigger>
        </TabsList>
        <TabsContent value="image" className="mt-4"><ImageScannerTab /></TabsContent>
        <TabsContent value="deep"  className="mt-4"><DeepDiveTab /></TabsContent>
        <TabsContent value="watch" className="mt-4"><WatchlistTab /></TabsContent>
        <TabsContent value="alerts" className="mt-4"><AlertsTab /></TabsContent>
        <TabsContent value="brief" className="mt-4"><BriefTab /></TabsContent>
        <TabsContent value="hot"   className="mt-4"><HotMarketsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
